import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import GoldButton from "./GoldButton";

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.13, delayChildren: 0.15 } },
};
const lineUp = {
  hidden: { y: "115%" },
  visible: { y: "0%", transition: { duration: 1.15, ease: [0.16, 1, 0.3, 1] } },
};
const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero({ started }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pb-24 pt-32 md:px-14"
    >
      <motion.div style={{ y, opacity }} className="mx-auto w-full max-w-7xl">
        <motion.div variants={container} initial="hidden" animate={started ? "visible" : "hidden"}>
          <motion.p
            variants={fade}
            className="mb-8 text-[11px] tracking-[0.45em] text-[#BDBDB6] md:text-sm"
          >
            WEBDESIGN-AGENTUR · AUGSBURG & UMGEBUNG
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display text-[11.5vw] font-extrabold uppercase leading-[0.98] tracking-tight md:text-[6.4vw]"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span variants={lineUp} className="block">
                Webdesign
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span variants={lineUp} className="block">
                aus Augsburg für
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-3">
              <motion.span variants={lineUp} className="block">
                Websites, die{" "}
                <em className="gold-text pr-2 font-serif-accent normal-case italic">auffallen.</em>
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={fade}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-[#BDBDB6] md:text-lg"
          >
            Ihre Website soll nicht nur online sein. Wir gestalten und entwickeln individuelle
            Websites für Handwerk, Gastronomie, Praxen, Kanzleien und Dienstleister in Augsburg
            und der Region — von der ersten Idee bis zum letzten Pixel.
          </motion.p>

          <motion.div variants={fade} className="mt-10 flex flex-wrap items-center gap-4">
            <GoldButton href="#kontakt" testId="hero-cta-primary">
              Kostenlose Erstberatung
            </GoldButton>
            <GoldButton href="#projekte" variant="outline" testId="hero-cta-secondary">
              Projekte entdecken
            </GoldButton>
          </motion.div>

          <motion.div
            variants={fade}
            data-testid="hero-stats"
            className="mt-16 grid grid-cols-1 gap-5 border-t border-white/10 pt-6 sm:grid-cols-3"
          >
            <p className="text-sm leading-relaxed text-[#BDBDB6]">
              <span className="gold-text mr-2 font-display text-lg font-bold">100 %</span>
              Individuelles Design, keine Baukasten-Vorlage
            </p>
            <p className="text-sm leading-relaxed text-[#BDBDB6]">
              <span className="gold-text mr-2 font-display text-lg font-bold">&lt; 24 Std.</span>
              Antwortzeit auf Ihre Anfrage
            </p>
            <p className="text-sm leading-relaxed text-[#BDBDB6]">
              <span className="gold-text mr-2 font-display text-lg font-bold">Augsburg</span>
              Vor Ort in Augsburg, Königsbrunn, Gersthofen und Umgebung
            </p>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: started ? 1 : 0 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 flex -translate-x-1/2 flex-col items-center gap-3"
        data-testid="scroll-indicator"
      >
        <span className="text-[10px] tracking-[0.45em] text-[#8A8A84]">LANGSAM SCROLLEN</span>
        <div className="h-10 w-px overflow-hidden bg-white/10">
          <motion.div
            className="h-1/2 w-full bg-[#D4AF37]"
            animate={{ y: ["-100%", "220%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
}
